/**
 * The hidden ☰ menu in a panel head: the button opens a small dropdown under it, and
 * an outside press, Esc, or choosing an item closes it again. Listens on the window in
 * the capture phase because the map stops pointer events before they reach the document.
 */

interface MenuButton {
  addEventListener(type: "click", fn: (event: Event) => void): void;
  setAttribute(name: string, value: string): void;
  contains(node: unknown): boolean;
  focus(): void;
}

interface MenuPanel {
  hidden: boolean;
  addEventListener(type: "click", fn: (event: Event) => void): void;
  contains(node: unknown): boolean;
}

interface MenuWindow {
  addEventListener(type: "pointerdown" | "keydown", fn: (event: Event) => void, capture?: boolean): void;
}

export interface PanelMenu {
  open(): void;
  close(): void;
  isOpen(): boolean;
}

export function bindPanelMenu(button: MenuButton, menu: MenuPanel, win: MenuWindow): PanelMenu {
  const set = (open: boolean) => {
    menu.hidden = !open;
    button.setAttribute("aria-expanded", String(open));
  };
  set(false);
  button.addEventListener("click", () => set(menu.hidden));
  menu.addEventListener("click", (event) => {
    const target = event.target as { closest?: (sel: string) => unknown } | null;
    if (target?.closest?.('[role="menuitem"]')) set(false);
  });
  win.addEventListener(
    "pointerdown",
    (event) => {
      if (menu.hidden) return;
      if (button.contains(event.target) || menu.contains(event.target)) return;
      set(false);
    },
    true,
  );
  win.addEventListener("keydown", (event) => {
    if (menu.hidden || (event as KeyboardEvent).key !== "Escape") return;
    event.stopPropagation();
    set(false);
    button.focus();
  }, true);
  return { open: () => set(true), close: () => set(false), isOpen: () => !menu.hidden };
}
